import { useState } from "react";

import { faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { Player } from "@lottiefiles/react-lottie-player";
import { Stack, TextField, Typography } from "@mui/material";
import axios from "axios";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { CountryData } from "react-phone-input-2";
import { useQueryClient } from "react-query";
import { NavLink } from "react-router-dom";
import invariant from "ts-invariant";

import { useGetUsStates } from "api/hooks/useGetUsStates";
import type { Ailment, Location } from "api/schemas/shared/Treatment";
import { usePostTherapistInvitation } from "api/services/therapist/hooks";
import { GET_THERAPIST_INVITATIONS_QUERY_KEY } from "api/services/therapist/hooks/useGetTherapistInvitations";
import { type FastTrackPayload, makeFastTrackPayloadSchema } from "api/services/therapist/schemas/invitations";
import firstConfettiAnimation from "assets/animations/confetti.json";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUISelect } from "shared/atoms/MUI";
import { Notification } from "shared/atoms/Notification";
import { PhoneNumberInput } from "shared/atoms/PhoneNumberInput";
import { WidgetBox } from "shared/containers/WidgetBox";
import { LanguageTypes, MarketTypes } from "types";
import { BrazeEvents, BrazeService } from "utils/braze";
import { getNormalizedMarket } from "utils/market/marketHelper";

import type { TreatmentOption } from "./options";
import { useTreatmentOptions, useTreatmentTypeOptions } from "./options";

type Status = {
  isError: boolean;
  label: string;
  patientId?: number;
};

export const FastTrack: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  const treatmentAreaOptions = useTreatmentOptions();
  const treatmentTypeOptions = useTreatmentTypeOptions();
  const [status, setStatus] = useState<Status>({ isError: false, label: "" });
  const [formResetKey, setFormResetKey] = useState(0);
  const [selectedPainLocation, setSelectedPainLocation] = useState<TreatmentOption | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const postTherapistInvitation = usePostTherapistInvitation();

  invariant(profile);
  const market = getNormalizedMarket(profile.market);
  const isSE = market === MarketTypes.SE;
  const isUS = market === MarketTypes.US;

  const { data: usStates } = useGetUsStates({ enabled: isUS });
  const [selectedUsState, setSelectedUsState] = useState<{ name: string; code: string } | null>(null);

  const hcoUsState = profile.health_care_organization?.us_state ?? null;
  const usStateLockedFromHco = isUS && Boolean(hcoUsState?.code);

  const form = useForm<FastTrackPayload>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      country_code: market ?? "",
      email: "",
      first_name: "",
      last_name: "",
      language_code: profile?.language_code || LanguageTypes.en,
      ...(isSE && { personal_number: "" }),
      ...(isUS && { us_state: hcoUsState?.code ?? "" }),
      treatment_type: treatmentTypeOptions.some(option => option.value === "rtm") ? "rtm" : "digital",
    },
    resolver: zodResolver(makeFastTrackPayloadSchema({ requirePersonalNumber: isSE, requireUsState: isUS })),
  });
  const {
    formState: { errors },
  } = form;

  const onSubmit = form.handleSubmit(async formData => {
    try {
      await postTherapistInvitation.mutateAsync(
        { payload: formData },
        {
          onSuccess: data => {
            queryClient.invalidateQueries([GET_THERAPIST_INVITATIONS_QUERY_KEY]);
            setShowConfetti(true);
            setStatus({ isError: false, label: t("invitations.form.success"), patientId: data.patient_id });

            BrazeService.track(BrazeEvents.PATIENT_INVITED);

            setTimeout(() => {
              setStatus({ isError: false, label: "" });
            }, 10000);

            form.reset();
            setFormResetKey(k => k + 1);
            setSelectedPainLocation(null);
            setSelectedUsState(null);
          },
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        switch (error?.response?.data?.error) {
          case "invitation_exists":
            setStatus({ isError: true, label: t("invitations.form.errors.invitation_exists") });
            break;
          case "invalid phone number":
            setStatus({ isError: true, label: t("invitations.form.errors.invalid_phone_number") });
            break;
          default:
            setStatus({ isError: true, label: t("errors.generic_contact_support") });
        }
      } else {
        setStatus({ isError: true, label: t("errors.generic_contact_support") });
        setTimeout(() => setStatus({ isError: false, label: "" }), 3000);
      }
    }
  });

  const defaultCountryCode = profile.market?.toLocaleLowerCase();

  return (
    <WidgetBox
      title={t("invitations.form.title")}
      description={t("invitations.form.description")}
      icon={<FontAwesomeIcon icon={faUserPlus} />}
      color="blue"
    >
      <FormProvider {...form}>
        <form id="quick-sign-up-form" onSubmit={onSubmit}>
          <Stack spacing={1.5} sx={{ backgroundColor: "#fff", borderRadius: 1.5 }}>
            <Typography variant="body1">{t("invitations.form.patient_details")}</Typography>
            <Stack direction="row" spacing={2}>
              <Controller
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    label={t("form.first_name")}
                    error={Boolean(errors.first_name)}
                    helperText={Boolean(errors.first_name) && t("errors.field.required")}
                    fullWidth
                  />
                )}
              />
              <Controller
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    label={t("form.last_name")}
                    error={Boolean(errors.last_name)}
                    helperText={Boolean(errors.last_name) && t("errors.field.required")}
                    fullWidth
                  />
                )}
              />
            </Stack>

            {isSE && (
              <Controller
                name="personal_number"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={e => {
                      const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 12);
                      const formatted = digits.length > 8 ? `${digits.slice(0, 8)}-${digits.slice(8)}` : digits;
                      field.onChange(formatted);
                    }}
                    type="text"
                    inputProps={{ maxLength: 13 }}
                    label={t("invitations.form.personal_number")}
                    error={Boolean(errors.personal_number)}
                    helperText={Boolean(errors.personal_number) && t("errors.field.required")}
                  />
                )}
              />
            )}

            <Controller
              control={form.control}
              name="email"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="email"
                  label={t("form.email")}
                  error={Boolean(errors.email)}
                  helperText={Boolean(errors.email) && t("errors.field.required")}
                />
              )}
            />

            <Controller
              key={formResetKey}
              name="phone_number"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <PhoneNumberInput
                  country={defaultCountryCode}
                  value={value}
                  onChange={(inputValue, data: CountryData, event) => {
                    event.stopPropagation();
                    form.setValue("phone_country_code", data.countryCode.toLocaleUpperCase());
                    onChange(inputValue);
                  }}
                  error={Boolean(errors.phone_number)}
                  helperText={errors.phone_number ? t("errors.field.required") : undefined}
                />
              )}
            />

            <MUISelect
              data-testid="dropdown-ailment"
              label={t("invitations.form.ailment")}
              options={treatmentAreaOptions}
              value={selectedPainLocation?.value ?? ""}
              onChange={event => {
                const { value } = event.target;
                const selected = treatmentAreaOptions.find(option => option.value === value);
                if (selected) {
                  form.setValue("ailment", selected.ailment as Ailment, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  form.setValue(
                    "pain_location",
                    selected.ailment === "joint_pain" ? (selected.value as Location) : undefined,
                    {
                      shouldDirty: true,
                      shouldValidate: true,
                    }
                  );
                  form.setValue("lateral_location", "undefined");
                  setSelectedPainLocation(selected);
                }
              }}
              error={Boolean(errors.ailment)}
              formHelperText={errors.ailment ? t("errors.field.required") : undefined}
            />

            {selectedPainLocation?.hasLateralLocation && (
              <Controller
                name="lateral_location"
                control={form.control}
                render={({ field }) => (
                  <MUISelect
                    {...field}
                    label={t("invitations.form.lateral_location.label")}
                    options={[
                      { label: t("invitations.form.lateral_location.left"), value: "left" },
                      { label: t("invitations.form.lateral_location.right"), value: "right" },
                    ]}
                    error={Boolean(errors.lateral_location)}
                    formHelperText={errors.lateral_location ? t("errors.field.required") : undefined}
                  />
                )}
              />
            )}

            {isUS && !usStateLockedFromHco && (
              <MUISelect
                label={t("invitations.form.us_state")}
                options={usStates?.map(s => ({ label: s.name, value: s.code })) ?? []}
                value={selectedUsState?.code ?? ""}
                onChange={event => {
                  const state = usStates?.find(s => s.code === event.target.value);
                  if (state) {
                    form.setValue("us_state", state.code, { shouldDirty: true, shouldValidate: true });
                    setSelectedUsState(state);
                  }
                }}
                error={Boolean(errors.us_state)}
                formHelperText={errors.us_state ? t("errors.field.required") : undefined}
              />
            )}

            {treatmentTypeOptions.length > 0 && (
              <Controller
                name="treatment_type"
                control={form.control}
                render={({ field }) => (
                  <MUISelect
                    {...field}
                    label={t("invitations.form.treatment_type")}
                    options={treatmentTypeOptions}
                    error={Boolean(errors.treatment_type)}
                    formHelperText={errors.treatment_type ? t("errors.field.required") : undefined}
                  />
                )}
              />
            )}

            <BaseButton
              type="submit"
              text={t("invitations.form.submit")}
              disabled={postTherapistInvitation.isLoading}
              loading={postTherapistInvitation.isLoading}
            />

            {status.label && (
              <Notification variant={status.isError ? "danger" : "success"} style={{ zIndex: 1000 }}>
                <Stack data-testid={status.isError ? "invite-error" : "invite-success"}>
                  <Typography variant="body2">{status.label}</Typography>
                  {status.patientId && (
                    <NavLink to={`/patients/${status.patientId}`}>{t("invitations.form.view_patient")}</NavLink>
                  )}
                </Stack>
              </Notification>
            )}
          </Stack>
        </form>
      </FormProvider>

      {showConfetti && (
        <Player
          autoplay
          data-testid="confetti-animation"
          src={firstConfettiAnimation}
          onEvent={event => {
            if (event === "complete") {
              setShowConfetti(false);
            }
          }}
          style={{ position: "absolute", left: 0, right: 0, bottom: 0 }}
        />
      )}
    </WidgetBox>
  );
};
